import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import {
  WhatsappLogo,
  CheckCircle,
  Lightning,
  Clock,
  ShieldCheck,
  Info,
} from "@phosphor-icons/react";
import MagneticButton from "@/components/MagneticButton";
import PilotCarIcon from "@/components/PilotCarIcon";
import SEOHead from "@/components/SEOHead";

const WHATSAPP_NUMBER = "31620933773";
const SITE_URL = "https://www.rijschool-autopilot.nl";

// Prices (kept in EUR — same for all languages)
const PRICE_PER_LESSON = 60;
const PRICE_EXAM = 300;          // praktijkexamen
const PRICE_TTT = 275;           // tussentijdse toets
const PRICE_BNOR = 350;          // BNOR examen
const PRICE_FAALANGST = 385;     // faalangstexamen

const fmtPrice = (n) =>
  `€${n.toLocaleString("nl-NL", { minimumFractionDigits: 0, maximumFractionDigits: 0 })},-`;

// ============================================================
// Translations
// ============================================================
const COPY = {
  nl: {
    seo: {
      title: "Spoedopleiding rijbewijs Eindhoven & Helmond — Sneller geslaagd | Rijschool AutoPilot",
      description:
        "Spoedopleiding rijbewijs in Eindhoven en Helmond. Bereken zelf je indicatieprijs met onze calculator. €60 per rijles, examen €300, BNOR €350. Drive like a pilot!",
      keywords:
        "spoedopleiding rijbewijs eindhoven, spoedopleiding rijbewijs helmond, spoedcursus rijles, snel rijbewijs halen, rijles in 4 weken, intensief rijles, BNOR examen, TTT tussentijdse toets, rijschool autopilot spoed",
      ogLocale: "nl_NL",
    },
    overline: "Spoedopleiding",
    titleA: "Sneller op de weg.",
    titleB: "Sneller geslaagd.",
    intro:
      "Heb je haast met je rijbewijs? Met onze spoedopleiding rij je in een compact traject — intensief, gestructureerd en met dezelfde rust en persoonlijke aandacht waar AutoPilot om bekend staat.",
    benefits: [
      { Icon: Lightning, title: "Compact traject", text: "Meerdere rijlessen per week, gericht plannen. Soms al binnen 4–8 weken examen." },
      { Icon: ShieldCheck, title: "Persoonlijk plan", text: "We stemmen het ritme af op jouw niveau en agenda. Geen one-size-fits-all." },
      { Icon: Clock, title: "Snel inplannen", text: "Versnelde aanvraag bij het CBR voor examen en TTT. Wij regelen het." },
    ],
    calcOverline: "Indicatieve prijs",
    calcTitleA: "Stel je eigen",
    calcTitleAccent: "spoedpakket",
    calcTitleB: "samen.",
    calcSub: "Schuif aan de balken en krijg meteen een indicatieve totaalprijs. Stuur je voorkeuren via WhatsApp — we nemen contact op om alles persoonlijk af te stemmen.",
    sliders: {
      desired: { label: "Gewenste rijlessen (60 min)", hintTpl: (v) => `${v} × €60` },
      already: { label: "Lessen die je al gehad hebt", hint: "puur ter info — helpt ons je niveau inschatten" },
      exams: { label: "Examens die je al gedaan hebt", hint: "puur ter info" },
    },
    extras: "Extra's",
    toggles: {
      ttt: { label: "Tussentijdse Toets (TTT) inbegrepen", hint: "Verplichte tussentoets via het CBR" },
      exam: { label: "Praktijkexamen inbegrepen", hint: "Aanmelding en reservering via het CBR" },
      bnor: { label: "BNOR examen (aangepast examen)", hint: "Aanmelding voor het CBR BNOR examen" },
      faalangst: { label: "Faalangstexamen", hint: "Officieel CBR-faalangstexamen" },
    },
    yourEstimate: "Jouw indicatie",
    totalLabel: "Geschatte totaalprijs",
    lineLessons: (n) => `${n} rijlessen × €60`,
    lineExam: "Praktijkexamen",
    lineTtt: "Tussentijdse Toets",
    lineBnor: "BNOR examen",
    lineFaalangst: "Faalangstexamen",
    ctaWa: "Aanvragen via WhatsApp",
    disclaimer:
      "Dit bedrag is een indicatie. De definitieve prijs stemmen we samen met jou af — afgestemd op jouw voortgang en doelen. Aan dit voorstel kunnen geen rechten worden ontleend.",
    tariff:
      "Tarieven: rijles 60 minuten · €60 · praktijkexamen · €300 · TTT · €275 · BNOR examen · €350 · faalangstexamen · €385. Prijzen kunnen variëren bij CBR-tariefwijzigingen. Heb je een specifieke wens? Stuur ons gerust een bericht — we denken met je mee.",
    includedOverline: "Wat zit erin",
    includedTitleA: "Wat je krijgt in een",
    includedAccent: "spoedopleiding.",
    included: [
      "Persoonlijk lesplan op maat van jouw niveau",
      "Compact lesplan, meerdere lessen per week",
      "Versnelde reservering CBR praktijkexamen of TTT",
      "Persoonlijke begeleiding van A tot Z",
      "Ophaalservice in Eindhoven & Helmond",
      "Heldere, transparante prijzen",
    ],
    waSummary: (s) =>
      [
        "*Aanvraag spoedopleiding*",
        "",
        `• Gewenste rijlessen: *${s.desired}*  (€60 p.u.)`,
        `• Lessen al gehad: *${s.alreadyTaken}*`,
        `• Examens al gehad: *${s.examsTaken}*`,
        `• Tussentijdse Toets: *${s.includeTtt ? "ja" : "nee"}*`,
        `• Praktijkexamen: *${s.includeExam ? "ja" : "nee"}*`,
        `• BNOR examen: *${s.includeBnor ? "ja" : "nee"}*`,
        `• Faalangstexamen: *${s.includeFaalangst ? "ja" : "nee"}*`,
        "",
        `Indicatieve prijs: *${fmtPrice(s.total)}*`,
        "",
        "Graag meer informatie over de mogelijkheden!",
      ].join("\n"),
  },
  tr: {
    seo: {
      title: "Hızlandırılmış sürücü kursu Eindhoven & Helmond — Daha hızlı ehliyet | Rijschool AutoPilot",
      description:
        "Eindhoven ve Helmond'da hızlandırılmış (spoed) sürücü kursu. Hesaplayıcımızla kendi tahmini fiyatınızı görün. Ders €60, sınav €300, BNOR €350. Drive like a pilot!",
      keywords:
        "hızlandırılmış sürücü kursu eindhoven, spoedopleiding türkçe, hızlı ehliyet eindhoven helmond, yoğun direksiyon dersi, kısa sürede ehliyet, BNOR sınavı, TTT ara sınav, türkçe rijschool eindhoven",
      ogLocale: "tr_TR",
    },
    overline: "Hızlandırılmış kurs",
    titleA: "Daha hızlı yola.",
    titleB: "Daha hızlı ehliyete.",
    intro:
      "Ehliyetiniz için acele mi ediyorsunuz? Hızlandırılmış kursumuzla kompakt bir programda sürersiniz — yoğun, planlı ve AutoPilot'un bilindiği sakin, kişisel ilgiyle.",
    benefits: [
      { Icon: Lightning, title: "Kompakt program", text: "Haftada birden çok ders, hedef odaklı planlama. Bazen 4–8 hafta içinde sınav." },
      { Icon: ShieldCheck, title: "Kişisel plan", text: "Ritmi seviyenize ve takviminize göre ayarlarız. Tek tip yaklaşım değil." },
      { Icon: Clock, title: "Hızlı planlama", text: "CBR sınavı ve TTT için hızlandırılmış başvuru. Hepsini biz hallederiz." },
    ],
    calcOverline: "Tahmini fiyat",
    calcTitleA: "Kendi",
    calcTitleAccent: "spoed paketinizi",
    calcTitleB: "oluşturun.",
    calcSub: "Çubukları kaydırın ve anında tahmini toplam fiyatı görün. Tercihlerinizi WhatsApp ile gönderin — sizinle iletişime geçip her şeyi kişisel olarak netleştiririz.",
    sliders: {
      desired: { label: "İstenen direksiyon dersi (60 dk)", hintTpl: (v) => `${v} × €60` },
      already: { label: "Daha önce aldığınız ders sayısı", hint: "yalnızca bilgi — seviyenizi anlamamıza yardımcı olur" },
      exams: { label: "Daha önce girdiğiniz sınav sayısı", hint: "yalnızca bilgi" },
    },
    extras: "Ekstralar",
    toggles: {
      ttt: { label: "Ara Sınav (TTT) dahil", hint: "CBR'nin zorunlu ara sınavı" },
      exam: { label: "Pratik sınav dahil", hint: "CBR üzerinden başvuru ve rezervasyon" },
      bnor: { label: "BNOR sınavı (uyarlanmış sınav)", hint: "CBR BNOR sınavı için başvuru" },
      faalangst: { label: "Sınav kaygısı sınavı (faalangst)", hint: "Resmi CBR sınav kaygısı sınavı" },
    },
    yourEstimate: "Tahmininiz",
    totalLabel: "Tahmini toplam fiyat",
    lineLessons: (n) => `${n} ders × €60`,
    lineExam: "Pratik sınav",
    lineTtt: "Ara Sınav (TTT)",
    lineBnor: "BNOR sınavı",
    lineFaalangst: "Sınav kaygısı sınavı",
    ctaWa: "WhatsApp ile başvur",
    disclaimer:
      "Bu tutar bir tahmindir. Kesin fiyat sizinle birlikte belirlenir — ilerlemenize ve hedeflerinize göre. Bu teklif üzerinden hak iddia edilemez.",
    tariff:
      "Tarifeler: 60 dk ders · €60 · pratik sınav · €300 · TTT · €275 · BNOR sınavı · €350 · sınav kaygısı sınavı · €385. CBR tarife değişikliklerinde fiyatlar değişebilir. Özel bir isteğiniz mi var? Bize yazın — birlikte düşünelim.",
    includedOverline: "Neler dahil",
    includedTitleA: "Hızlandırılmış kursta",
    includedAccent: "neler alırsınız.",
    included: [
      "Seviyenize özel kişisel ders planı",
      "Kompakt ders planı, haftada birden çok ders",
      "CBR pratik sınavı veya TTT için hızlı rezervasyon",
      "A'dan Z'ye kişisel rehberlik",
      "Eindhoven ve Helmond bölgesinde alma servisi",
      "Net ve şeffaf fiyatlandırma",
    ],
    waSummary: (s) =>
      [
        "*Spoed kurs başvurusu*",
        "",
        `• İstenen ders: *${s.desired}*  (€60 / saat)`,
        `• Alınan ders: *${s.alreadyTaken}*`,
        `• Girilen sınav: *${s.examsTaken}*`,
        `• Ara Sınav (TTT): *${s.includeTtt ? "evet" : "hayır"}*`,
        `• Pratik sınav: *${s.includeExam ? "evet" : "hayır"}*`,
        `• BNOR sınavı: *${s.includeBnor ? "evet" : "hayır"}*`,
        `• Sınav kaygısı sınavı: *${s.includeFaalangst ? "evet" : "hayır"}*`,
        "",
        `Tahmini fiyat: *${fmtPrice(s.total)}*`,
        "",
        "Olanaklar hakkında daha fazla bilgi rica ederim!",
      ].join("\n"),
  },
  en: {
    seo: {
      title: "Crash course driving licence Eindhoven & Helmond — Fast-track | Rijschool AutoPilot",
      description:
        "Crash / fast-track driving course in Eindhoven and Helmond. Build your estimate with our live calculator. Lesson €60, exam €300, BNOR €350. Drive like a pilot!",
      keywords:
        "crash course driving licence eindhoven, fast track driving lessons helmond, intensive driving course netherlands, quick driver's licence, BNOR exam, TTT interim test, english speaking driving school eindhoven",
      ogLocale: "en_US",
    },
    overline: "Crash course",
    titleA: "Faster on the road.",
    titleB: "Faster to pass.",
    intro:
      "Need your licence fast? Our crash course gives you a compact training — intensive, structured, and with the same calm, personal attention AutoPilot is known for.",
    benefits: [
      { Icon: Lightning, title: "Compact training", text: "Multiple lessons per week, targeted planning. Sometimes exam-ready within 4–8 weeks." },
      { Icon: ShieldCheck, title: "Personal plan", text: "We tune the pace to your level and schedule. No one-size-fits-all." },
      { Icon: Clock, title: "Fast scheduling", text: "Fast-tracked CBR exam and TTT booking. We handle it for you." },
    ],
    calcOverline: "Indicative price",
    calcTitleA: "Build your own",
    calcTitleAccent: "crash package.",
    calcTitleB: "",
    calcSub: "Drag the sliders and get an instant estimated total. Send your preferences via WhatsApp — we'll get in touch to finalise everything personally.",
    sliders: {
      desired: { label: "Desired lessons (60 min)", hintTpl: (v) => `${v} × €60` },
      already: { label: "Lessons you've already had", hint: "info only — helps us gauge your level" },
      exams: { label: "Exams you've already taken", hint: "info only" },
    },
    extras: "Add-ons",
    toggles: {
      ttt: { label: "Interim test (TTT) included", hint: "Mandatory interim test via the CBR" },
      exam: { label: "Practical exam included", hint: "Booking via the CBR" },
      bnor: { label: "BNOR exam (adapted exam)", hint: "Booking for the CBR BNOR exam" },
      faalangst: { label: "Exam-anxiety exam (faalangst)", hint: "Official CBR exam-anxiety test" },
    },
    yourEstimate: "Your estimate",
    totalLabel: "Estimated total",
    lineLessons: (n) => `${n} lessons × €60`,
    lineExam: "Practical exam",
    lineTtt: "Interim test (TTT)",
    lineBnor: "BNOR exam",
    lineFaalangst: "Exam-anxiety exam",
    ctaWa: "Request via WhatsApp",
    disclaimer:
      "This amount is an indication. The final price is agreed together with you — tailored to your progress and goals. No rights can be derived from this estimate.",
    tariff:
      "Rates: 60-minute lesson · €60 · practical exam · €300 · TTT · €275 · BNOR exam · €350 · exam-anxiety exam · €385. Prices may vary with CBR rate changes. Got a specific request? Send us a message — we'll think along.",
    includedOverline: "What's included",
    includedTitleA: "What you get in a",
    includedAccent: "crash course.",
    included: [
      "Personal lesson plan tailored to your level",
      "Compact lesson plan, multiple lessons per week",
      "Fast-tracked CBR practical or TTT booking",
      "Personal guidance from A to Z",
      "Pickup service in Eindhoven & Helmond",
      "Clear, transparent pricing",
    ],
    waSummary: (s) =>
      [
        "*Crash course request*",
        "",
        `• Desired lessons: *${s.desired}*  (€60 / hour)`,
        `• Lessons already taken: *${s.alreadyTaken}*`,
        `• Exams already taken: *${s.examsTaken}*`,
        `• Interim test (TTT): *${s.includeTtt ? "yes" : "no"}*`,
        `• Practical exam: *${s.includeExam ? "yes" : "no"}*`,
        `• BNOR exam: *${s.includeBnor ? "yes" : "no"}*`,
        `• Exam-anxiety exam: *${s.includeFaalangst ? "yes" : "no"}*`,
        "",
        `Indicative price: *${fmtPrice(s.total)}*`,
        "",
        "I'd love more info on the possibilities!",
      ].join("\n"),
  },
};

// ============================================================
// Custom range slider — gold luxury styling
// ============================================================
const LuxurySlider = ({
  id,
  label,
  hint,
  min,
  max,
  step = 1,
  value,
  onChange,
  unit = "",
  formatValue,
  accent = "var(--gold)",
}) => {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div className="space-y-2" data-testid={`slider-row-${id}`}>
      <div className="flex items-baseline justify-between gap-3">
        <label htmlFor={id} className="text-sm sm:text-base text-white tracking-tight font-medium">
          {label}
        </label>
        <div className="text-right">
          <div className="font-display-italic text-gold text-2xl sm:text-3xl leading-none">
            {formatValue ? formatValue(value) : `${value}${unit}`}
          </div>
        </div>
      </div>
      <input
        id={id}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="luxury-range w-full"
        style={{
          background: `linear-gradient(90deg, ${accent} 0%, ${accent} ${pct}%, rgba(255,255,255,0.10) ${pct}%, rgba(255,255,255,0.10) 100%)`,
        }}
        data-testid={`slider-${id}`}
        aria-label={label}
      />
      <div className="flex justify-between text-[10px] uppercase tracking-[0.2em] text-silver/60">
        <span>{min}{unit}</span>
        {hint && <span className="text-silver/70 text-[10px] normal-case tracking-normal">{hint}</span>}
        <span>{max}{unit}</span>
      </div>
    </div>
  );
};

// ============================================================
// Toggle row
// ============================================================
const ToggleRow = ({ id, label, hint, price, value, onChange }) => (
  <button
    type="button"
    onClick={() => onChange(!value)}
    className={`w-full text-left p-4 lg:p-5 border transition-all flex items-center gap-4 ${
      value
        ? "border-[rgba(224,192,104,0.6)] bg-[rgba(224,192,104,0.06)]"
        : "border-white/10 bg-white/[0.02] hover:border-white/25"
    }`}
    data-testid={`toggle-${id}`}
    aria-pressed={value}
  >
    <div
      className={`flex-shrink-0 w-6 h-6 grid place-items-center border transition-colors ${
        value ? "bg-gold border-gold" : "border-white/30"
      }`}
      aria-hidden="true"
    >
      {value && (
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path d="M2 6.5l2.5 2.5L10 3.5" stroke="#1a1f2b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </div>
    <div className="flex-1 min-w-0">
      <div className="text-white text-sm sm:text-base font-medium">{label}</div>
      {hint && <div className="text-xs text-silver/70 mt-0.5">{hint}</div>}
    </div>
    <div className="text-gold font-display-italic text-xl sm:text-2xl">+{fmtPrice(price)}</div>
  </button>
);

// ============================================================
// Main page — accepts `lang` prop
// ============================================================
const SpoedPage = ({ lang = "nl" }) => {
  const t = COPY[lang] || COPY.nl;
  const [desired, setDesired] = useState(30);
  const [alreadyTaken, setAlreadyTaken] = useState(0);
  const [examsTaken, setExamsTaken] = useState(0);
  const [includeTtt, setIncludeTtt] = useState(false);
  const [includeExam, setIncludeExam] = useState(true);
  const [includeBnor, setIncludeBnor] = useState(false);
  const [includeFaalangst, setIncludeFaalangst] = useState(false);

  const total = useMemo(
    () =>
      desired * PRICE_PER_LESSON +
      (includeTtt ? PRICE_TTT : 0) +
      (includeExam ? PRICE_EXAM : 0) +
      (includeBnor ? PRICE_BNOR : 0) +
      (includeFaalangst ? PRICE_FAALANGST : 0),
    [desired, includeTtt, includeExam, includeBnor, includeFaalangst]
  );

  const waMessage = useMemo(() => {
    return encodeURIComponent(
      t.waSummary({
        desired,
        alreadyTaken,
        examsTaken,
        includeTtt,
        includeExam,
        includeBnor,
        includeFaalangst,
        total,
      })
    );
  }, [t, desired, alreadyTaken, examsTaken, includeTtt, includeExam, includeBnor, includeFaalangst, total]);

  // Build canonical + hreflang for SEO
  const path = lang === "nl" ? "/spoed" : `/${lang}/spoed`;
  const canonical = `${SITE_URL}${path}`;
  const alternates = [
    { hreflang: "nl", href: `${SITE_URL}/spoed` },
    { hreflang: "tr", href: `${SITE_URL}/tr/spoed` },
    { hreflang: "en", href: `${SITE_URL}/en/spoed` },
    { hreflang: "x-default", href: `${SITE_URL}/spoed` },
  ];

  // JSON-LD Service schema for the crash course
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name:
      lang === "tr"
        ? "Hızlandırılmış sürücü kursu"
        : lang === "en"
        ? "Crash driving course"
        : "Spoedopleiding rijbewijs",
    serviceType:
      lang === "tr"
        ? "Hızlandırılmış direksiyon dersi"
        : lang === "en"
        ? "Fast-track driving lessons"
        : "Spoed rijopleiding",
    description: t.seo.description,
    provider: {
      "@type": "DrivingSchool",
      name: "Rijschool AutoPilot",
      url: SITE_URL,
      telephone: "+31620933773",
    },
    areaServed: [
      { "@type": "City", name: "Eindhoven" },
      { "@type": "City", name: "Helmond" },
    ],
    offers: {
      "@type": "AggregateOffer",
      priceCurrency: "EUR",
      lowPrice: "275",
      highPrice: "5000",
      offerCount: "5",
      offers: [
        { "@type": "Offer", name: "60-min rijles", price: "60", priceCurrency: "EUR" },
        { "@type": "Offer", name: "Tussentijdse Toets (TTT)", price: "275", priceCurrency: "EUR" },
        { "@type": "Offer", name: "Praktijkexamen", price: "300", priceCurrency: "EUR" },
        { "@type": "Offer", name: "BNOR examen", price: "350", priceCurrency: "EUR" },
        { "@type": "Offer", name: "Faalangstexamen", price: "385", priceCurrency: "EUR" },
      ],
    },
  };

  return (
    <>
      <SEOHead
        title={t.seo.title}
        description={t.seo.description}
        keywords={t.seo.keywords}
        lang={lang}
        canonical={canonical}
        ogLocale={t.seo.ogLocale}
        alternates={alternates}
        jsonLd={jsonLd}
      />

      {/* Header / banner */}
      <section
        className="relative pt-40 pb-12 px-6 md:px-12 overflow-hidden noise"
        data-testid={`spoed-banner-${lang}`}
      >
        <div className="absolute inset-0 -z-0">
          <div className="absolute -top-32 -right-20 w-[520px] h-[520px] rounded-full bg-[rgba(212,175,55,0.08)] blur-3xl" />
          <div className="absolute bottom-0 left-0 w-[380px] h-[380px] rounded-full bg-[rgba(212,175,55,0.05)] blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <span className="runway-stripes w-16 block" />
            <span className="overline flex items-center gap-2">
              <Lightning size={14} weight="fill" /> {t.overline}
            </span>
          </div>
          <h1
            className="text-5xl md:text-6xl lg:text-7xl tracking-tight font-light text-white leading-[1.05]"
            data-testid={`spoed-title-${lang}`}
          >
            {t.titleA}
            <br />
            <span className="font-display-italic text-gold text-6xl md:text-7xl lg:text-8xl">
              {t.titleB}
            </span>
          </h1>
          <p className="text-lg text-silver mt-6 max-w-2xl leading-relaxed">{t.intro}</p>
        </div>
      </section>

      {/* Benefits */}
      <section className="px-6 md:px-12 mb-12" data-testid={`spoed-why-${lang}`}>
        <div className="max-w-7xl mx-auto grid sm:grid-cols-3 gap-4">
          {t.benefits.map((c, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="card p-6"
            >
              <c.Icon size={26} weight="duotone" className="text-gold mb-3" />
              <div className="text-white text-lg tracking-tight mb-1">{c.title}</div>
              <div className="text-sm text-silver leading-relaxed">{c.text}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Calculator */}
      <section
        className="relative py-12 lg:py-20 px-6 md:px-12 bg-secondary"
        data-testid={`spoed-calc-${lang}`}
      >
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-10 max-w-3xl"
          >
            <div className="overline mb-4">{t.calcOverline}</div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl tracking-tight font-light text-white">
              {t.calcTitleA}{" "}
              <span className="font-display-italic text-gold text-4xl md:text-5xl lg:text-6xl">
                {t.calcTitleAccent}
              </span>
              {t.calcTitleB ? <> {t.calcTitleB}</> : null}
            </h2>
            <p className="text-silver mt-4 leading-relaxed">{t.calcSub}</p>
          </motion.div>

          <div className="grid lg:grid-cols-5 gap-8">
            <div className="lg:col-span-3 card p-7 lg:p-10 space-y-8">
              <LuxurySlider
                id="desired"
                label={t.sliders.desired.label}
                hint={t.sliders.desired.hintTpl(desired)}
                min={5}
                max={60}
                value={desired}
                onChange={setDesired}
              />
              <LuxurySlider
                id="already"
                label={t.sliders.already.label}
                hint={t.sliders.already.hint}
                min={0}
                max={80}
                value={alreadyTaken}
                onChange={setAlreadyTaken}
                accent="rgba(184, 191, 202, 0.85)"
              />
              <LuxurySlider
                id="exams"
                label={t.sliders.exams.label}
                hint={t.sliders.exams.hint}
                min={0}
                max={5}
                value={examsTaken}
                onChange={setExamsTaken}
                accent="rgba(184, 191, 202, 0.85)"
              />

              <div className="space-y-3 pt-2">
                <div className="overline">{t.extras}</div>
                <ToggleRow
                  id="ttt"
                  label={t.toggles.ttt.label}
                  hint={t.toggles.ttt.hint}
                  price={PRICE_TTT}
                  value={includeTtt}
                  onChange={setIncludeTtt}
                />
                <ToggleRow
                  id="exam"
                  label={t.toggles.exam.label}
                  hint={t.toggles.exam.hint}
                  price={PRICE_EXAM}
                  value={includeExam}
                  onChange={setIncludeExam}
                />
                <ToggleRow
                  id="bnor"
                  label={t.toggles.bnor.label}
                  hint={t.toggles.bnor.hint}
                  price={PRICE_BNOR}
                  value={includeBnor}
                  onChange={setIncludeBnor}
                />
                <ToggleRow
                  id="faalangst"
                  label={t.toggles.faalangst.label}
                  hint={t.toggles.faalangst.hint}
                  price={PRICE_FAALANGST}
                  value={includeFaalangst}
                  onChange={setIncludeFaalangst}
                />
              </div>
            </div>

            <div className="lg:col-span-2">
              <div className="card-highlight scan-line p-8 lg:p-10 flex flex-col gap-6 sticky top-28 overflow-hidden">
                <div className="flex items-center justify-between">
                  <span className="overline">{t.yourEstimate}</span>
                  <PilotCarIcon size={32} weight="fill" className="text-gold anim-salute" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.25em] text-silver/70 mb-2">
                    {t.totalLabel}
                  </div>
                  <motion.div
                    key={total}
                    initial={{ opacity: 0.4, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                    className="text-5xl lg:text-6xl font-extralight text-white leading-none"
                    data-testid={`spoed-total-${lang}`}
                  >
                    {fmtPrice(total)}
                  </motion.div>
                </div>

                <ul className="text-sm text-silver space-y-2 border-t border-white/10 pt-5">
                  <li className="flex justify-between">
                    <span>{t.lineLessons(desired)}</span>
                    <span className="text-white">{fmtPrice(desired * 60)}</span>
                  </li>
                  {includeTtt && (
                    <li className="flex justify-between">
                      <span>{t.lineTtt}</span>
                      <span className="text-white">{fmtPrice(PRICE_TTT)}</span>
                    </li>
                  )}
                  {includeExam && (
                    <li className="flex justify-between">
                      <span>{t.lineExam}</span>
                      <span className="text-white">{fmtPrice(PRICE_EXAM)}</span>
                    </li>
                  )}
                  {includeBnor && (
                    <li className="flex justify-between">
                      <span>{t.lineBnor}</span>
                      <span className="text-white">{fmtPrice(PRICE_BNOR)}</span>
                    </li>
                  )}
                  {includeFaalangst && (
                    <li className="flex justify-between">
                      <span>{t.lineFaalangst}</span>
                      <span className="text-white">{fmtPrice(PRICE_FAALANGST)}</span>
                    </li>
                  )}
                </ul>

                <div className="mt-2">
                  <MagneticButton>
                    <a
                      href={`https://wa.me/${WHATSAPP_NUMBER}?text=${waMessage}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-gold w-full justify-center"
                      data-testid={`spoed-cta-wa-${lang}`}
                    >
                      <WhatsappLogo size={18} weight="bold" />
                      {t.ctaWa}
                    </a>
                  </MagneticButton>
                </div>

                <div className="flex items-start gap-3 pt-2 border-t border-white/10">
                  <Info size={16} weight="duotone" className="text-gold flex-shrink-0 mt-0.5" />
                  <p className="text-[12px] text-silver/80 leading-relaxed">{t.disclaimer}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-10 text-xs text-silver/60 max-w-3xl">{t.tariff}</div>
        </div>
      </section>

      {/* Included */}
      <section className="py-20 px-6 md:px-12" data-testid={`spoed-included-${lang}`}>
        <div className="max-w-5xl mx-auto">
          <div className="overline mb-4">{t.includedOverline}</div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl tracking-tight font-light text-white mb-8">
            {t.includedTitleA}{" "}
            <span className="font-display-italic text-gold text-4xl md:text-5xl lg:text-6xl">
              {t.includedAccent}
            </span>
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {t.included.map((f, i) => (
              <div key={i} className="flex items-start gap-3 p-4 border border-white/8 bg-white/[0.02]">
                <CheckCircle size={20} weight="duotone" className="text-gold flex-shrink-0 mt-0.5" />
                <span className="text-silver-light text-sm leading-relaxed">{f}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default SpoedPage;
